import $api2 from '@/plugins/axiosInstance2.js'

const BASE = '/admin/youtube-cid'

export const YOUTUBE_CMS_CONTENT_OWNER_ID = 'aSLJKViaqgcDwiqaaPLf2A'

export const KNOWN_CONTENT_OWNERS = {
  [YOUTUBE_CMS_CONTENT_OWNER_ID]: 'yt-soundrepublicainc',
}

export function resolveContentOwnerName(ownerId) {
  if (!ownerId) return ''
  return KNOWN_CONTENT_OWNERS[ownerId] || ownerId
}

export function youtubeCmsAssetUrl(assetId) {
  if (!assetId) return ''
  return `https://studio.youtube.com/asset/${assetId}/metadata/overview?o=${YOUTUBE_CMS_CONTENT_OWNER_ID}`
}

export function youtubeCmsAssetAnalyticsUrl(assetId) {
  if (!assetId) return ''
  return `https://studio.youtube.com/asset/${assetId}/analytics/tab-overview/period-default?o=${YOUTUBE_CMS_CONTENT_OWNER_ID}`
}

function unwrap(res) {
  return res && res.data && Object.prototype.hasOwnProperty.call(res.data, 'data') ? res.data.data : res && res.data
}

export const YoutubeCidApi = {
  // ---- Assets ----
  searchAssets(params = {}) {
    return $api2.get(`${BASE}/assets/search`, { params }).then(unwrap)
  },
  getAsset(assetId) {
    return $api2.get(`${BASE}/assets/${encodeURIComponent(assetId)}`).then(unwrap)
  },
  getOwnership(assetId) {
    return $api2.get(`${BASE}/assets/${encodeURIComponent(assetId)}/ownership`).then(unwrap)
  },
  getMetadataHistory(assetId) {
    return $api2.get(`${BASE}/assets/${encodeURIComponent(assetId)}/metadata-history`).then(unwrap)
  },
  getAssetShares(assetId) {
    return $api2.get(`${BASE}/assets/${encodeURIComponent(assetId)}/shares`).then(unwrap)
  },
  getMatchPolicy(assetId) {
    return $api2.get(`${BASE}/assets/${encodeURIComponent(assetId)}/match-policy`).then(unwrap)
  },
  updateMatchPolicy(assetId, body) {
    return $api2.patch(`${BASE}/assets/${encodeURIComponent(assetId)}/match-policy`, body).then(unwrap)
  },

  // ---- Claims ----
  searchClaims(params = {}) {
    return $api2.get(`${BASE}/claims/search`, { params }).then(unwrap)
  },
  getClaim(claimId) {
    return $api2.get(`${BASE}/claims/${encodeURIComponent(claimId)}`).then(unwrap)
  },
  getClaimHistory(claimId) {
    return $api2.get(`${BASE}/claims/${encodeURIComponent(claimId)}/history`).then(unwrap)
  },
  updateClaim(claimId, body) {
    return $api2.patch(`${BASE}/claims/${encodeURIComponent(claimId)}`, body).then(unwrap)
  },

  // ---- References ----
  listReferences(params = {}) {
    return $api2.get(`${BASE}/references`, { params }).then(unwrap)
  },
  getReference(referenceId) {
    return $api2.get(`${BASE}/references/${encodeURIComponent(referenceId)}`).then(unwrap)
  },
  uploadReference({ assetId, claimId, contentType, file }) {
    const form = new FormData()
    if (assetId) form.append('assetId', assetId)
    if (claimId) form.append('claimId', claimId)
    if (contentType) form.append('contentType', contentType)
    if (file) form.append('file', file)
    return $api2
      .post(`${BASE}/references`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(unwrap)
  },
  patchReference(referenceId, body) {
    return $api2.patch(`${BASE}/references/${encodeURIComponent(referenceId)}`, body).then(unwrap)
  },

  // ---- Policies ----
  listPolicies() {
    return $api2.get(`${BASE}/policies`).then(unwrap)
  },
  getPolicy(policyId) {
    return $api2.get(`${BASE}/policies/${encodeURIComponent(policyId)}`).then(unwrap)
  },

  // ---- Whitelists ----
  listWhitelists(params = {}) {
    return $api2.get(`${BASE}/whitelists`, { params }).then(unwrap)
  },
  getWhitelist(channelId) {
    return $api2.get(`${BASE}/whitelists/${encodeURIComponent(channelId)}`).then(unwrap)
  },
  addWhitelist({ id, title }) {
    return $api2.post(`${BASE}/whitelists`, { id, title }).then(unwrap)
  },
  removeWhitelist(channelId) {
    return $api2.delete(`${BASE}/whitelists/${encodeURIComponent(channelId)}`)
  },
}

// Heuristic ID-shape detection for the search box.
const ASSET_ID_RE = /^A\d{10,}$/i
const ISRC_RE = /^[A-Z]{2}[A-Z0-9]{3}\d{7}$/i
const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/
const CHANNEL_ID_RE = /^UC[A-Za-z0-9_-]{20,}$/

export function detectIdShape(input) {
  const v = (input || '').trim()
  if (!v) return null
  if (ASSET_ID_RE.test(v)) return 'asset'
  if (ISRC_RE.test(v)) return 'isrc'
  if (CHANNEL_ID_RE.test(v)) return 'channel'
  if (VIDEO_ID_RE.test(v)) return 'video'
  return 'custom'
}

export const RECENT_KEY = 'yt_cid_recent_searches'

export function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    return raw ? JSON.parse(raw) : []
  } catch (e) {
    return []
  }
}

export function pushRecent(entry) {
  if (!entry || !entry.term) return
  const list = loadRecent().filter((e) => e.term !== entry.term)
  list.unshift({ ...entry, at: Date.now() })
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 20)))
  } catch (e) {
    /* noop */
  }
}
